<template>
  <v-data-table
    :headers="headers"
    :items="items"
    sort-by="signature.date"
    sort-desc
    :items-per-page="-1"
    fixed-header
    dense
    hide-default-footer
    :no-data-text="$t('profile.history.noData')"
    group-by="mentorshipId"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-subheader class="subtitle font-weight-bold text-uppercase">{{
          $t("profile.subheader.mentorship")
        }}</v-subheader>
        <v-spacer></v-spacer>
      </v-toolbar>
    </template>
    <template v-slot:[`item.mentor`]="{ item }">
      <span>{{ item.mentor | nameId }}</span>
    </template>
    <template v-slot:[`item.signature.employee`]="{ item }">
      <span>{{ item.signature.employee | nameId }}</span>
    </template>
    <template v-slot:[`item.signature.type`]="{ item }">
      <span>{{ item.signature.type }}</span>
    </template>
    <template v-slot:[`item.signature.date`]="{ item }">
      <span>{{ item.signature.date | dateTime }}</span>
    </template>
    <template v-slot:[`item.mentorship.duration`]="{ item }">
      <span>{{
        $tc(
          "profile.mentorship.duration",
          Math.ceil(item.mentorship.duration / (60 * 60 * 24))
        )
      }}</span>
    </template>
    <template v-slot:[`item.signature.rereading`]="{ item }">
      <span>{{ item.signature.rereading }}</span>
    </template>

    <template v-slot:[`item.to`]="{ item }">
      <router-link :to="item.to" target="_blank" v-if="item.to">{{
        item.to.description
      }}</router-link>
    </template>

    <template v-slot:[`group.header`]="{ group, items }">
      <th :colspan="headers.length">
        {{ getHeaderText(getGroupItems(group, items)) }}
        <span
          v-if="items[items.length - 1].mentorship.deactivatedDateTime"
          style="color: red"
          >{{ getDeactivatedText(getGroupItems(group, items)) }}</span
        >
      </th>
    </template>
  </v-data-table>
</template>

<script>
export default {
  props: {
    mentorshipHistory: {
      type: Array,
      required: true,
    },
  },
  computed: {
    headers() {
      const headerClass = "text-uppercase subheading text-truncate";
      return [
        {
          text: this.$i18n.t("profile.mentorship.column.tag"),
          value: "area",
          class: headerClass,
        },
        {
          text: this.$i18n.t("profile.history.topic"),
          value: "topic",
          class: headerClass,
        },
        {
          text: this.$i18n.t("employees.column.name"),
          value: "signature.employee",
          class: headerClass,
        },
        {
          text: this.$i18n.t("instructions.column.signature"),
          value: "signature.type",
          class: headerClass,
        },
        {
          text: this.$i18n.t("instructions.column.signatureDate"),
          value: "signature.date",
          class: headerClass,
        },
        {
          text: this.$i18n.t("profile.mentorship.column.mentor"),
          value: "mentor",
          class: headerClass,
        },
        {
          text: this.$i18n.t("instructions.column.reason"),
          value: "signature.reason",
          class: headerClass,
        },
        {
          text: this.$i18n.t("profile.history.reference"),
          value: "to",
          class: headerClass,
          align: "right",
        },
      ];
    },
    items() {
      let items = [];
      this.mentorshipHistory &&
        this.mentorshipHistory.forEach((ms) => {
          let item = {
            id: "t" + ms.tag.id + "ms" + ms.id,
            area: ms.tag.description || ms.tag.name,
            mentorshipId: ms.id,
            mentorship: {
              duration: ms.duration,
              start: ms.startedAt,
              activeSince: ms.activeSince,
              deactivatedDateTime: ms.deactivatedDateTime,
              deactivatedByEmployeeId: ms.deactivatedByEmployeeId,
              reason: ms.reason,
            },
            mentor: ms.mentor,
            mentee: ms.employee,

            to: null,
            topic: "",
            signature: {
              employee: "",
              type: "",
              date: "",
            },
          };
          this.mapSignatures(
            ms.signatures,
            item,
            items,
            this.$i18n.t(`mentorship.title`),
            null
          );
          this.mapActions(ms.actions, item, items);
          this.mapFeedbacks(ms.feedbacks, item, items);
        });
      return items;
    },
  },

  methods: {
    mapActions(actions, msItem, items) {
      actions &&
        actions.assignedVersions &&
        actions.assignedVersions.length > 0 &&
        actions.assignedVersions.slice().forEach((av) => {
          av.content.forEach((c, index) => {
            const topic = `${actions.code} ${
              av.revision
            }(${this.$options.filters.date(av.created)}) ${av.name.name} `;
            const to = {
              name: "mentorship-actions-assigned",
              params: { id: msItem.mentorshipId },
              query: { language: this.$i18n.locale },
              description: `${index + 1} ${c.contentLocale.title}  ID(${
                msItem.mentorshipId
              })`,
            };
            this.mapSignatures(c.signatures, msItem, items, topic, to);
          });
        });
    },
    mapFeedbacks(feedbacks, msItem, items) {
      feedbacks &&
        feedbacks.length > 0 &&
        feedbacks.slice().forEach((f) => {
          const mentorItem = { ...msItem };

          if (f.mentorSubmittedForm || f.menteeSubmittedForm) {
            mentorItem.to = {
              name: "mentorship-feedback-assigned",
              params: { id: f.assignedId },
              query: { language: this.$i18n.locale },
              description: `${f.code} ${
                f.revision
              }(${this.$options.filters.date(f.revisionCreated)}) ID(${
                f.assignedId
              })`,
            };
          }

          mentorItem.topic = `${this.$i18n.t(
            `profile.mentorship.feedback.title`
          )} ${this.$i18n.t(`profile.mentorship.feedback.stage`)} ${f.stage}`;

          const menteeItem = { ...mentorItem };
          mentorItem.id += "fb" + f.id + "mentor";
          menteeItem.id += "fb" + f.id + "mentee";

          const sigType = this.$i18n.t(`profile.history.signature.submit`);
          mentorItem.signature = {
            date: f.mentorSubmittedForm && f.mentorSubmittedForm.dateTime,
            type: sigType,
            employee: f.mentor,
          };
          menteeItem.signature = {
            date: f.menteeSubmittedForm && f.menteeSubmittedForm.dateTime,
            type: sigType,
            employee: f.mentee,
          };

          items.push(mentorItem);
          items.push(menteeItem);
        });
    },
    mapSignatures(sigs, msItem, items, topic, to) {
      sigs &&
        sigs.length > 0 &&
        sigs.slice().forEach((s) => {
          const item = { ...msItem };
          const sig = {
            id: s.id,
            date: s.date || s.dateTime,
            type: `${this.$i18n.t(`profile.history.signature.${s.type}`)}`,
            reason: s.reason,
            employee: s.employee,
            signatures: s.signatures,
          };

          if (s.mentor) {
            item.mentor = s.mentor;
          }

          if (to) {
            item.to = to;
          }

          item.id += "s" + s.id;
          item.topic = topic;
          item.signature = sig;
          items.push(item);

          if (
            item.signature.signatures &&
            item.signature.signatures.length > 0
          ) {
            this.mapSignatures(sig.signatures, msItem, items, topic, to);
          }
        });
    },

    getGroupItems(group, items) {
      return items.filter((i) => i.mentorshipId == group);
    },
    getHeaderText(groupItems) {
      if (groupItems.length == 0) {
        return "";
      }
      const last = groupItems[groupItems.length - 1];

      const idText = `ID ${last.mentorshipId}`;
      const areaText = `${this.$i18n.t("profile.mentorship.column.tag")} ${
        last.area
      }`;

      const mentorText = `${this.$i18n.t(
        "profile.mentorship.column.mentor"
      )} ${this.$options.filters.nameId(last.mentor)}`;

      const startedText = `${this.$i18n.t(
        "profile.mentorship.column.start"
      )} ${this.$options.filters.date(last.mentorship.start)}`;

      const durationText = `${this.$i18n.t(
        "profile.mentorship.column.duration"
      )} ${this.$i18n.tc(
        "profile.mentorship.duration",
        Math.ceil(last.mentorship.duration / (60 * 60 * 24))
      )}`;

      let activeSinceText = "";
      if (last.mentorship.activeSince) {
        activeSinceText = `${this.$i18n.t(
          "profile.mentorship.column.activeSince"
        )} ${this.$options.filters.date(last.mentorship.activeSince)}`;
      }

      let isActiveText = "";
      if (last.mentorship.isActive) {
        isActiveText = `${this.$i18n.t(
          "profile.mentorship.column.isActive"
        )} ${this.$options.filters.date(last.mentorship.isActive)}`;
      }

      return `${idText} ${areaText} ${mentorText} ${startedText} ${durationText} ${activeSinceText} ${isActiveText}`;
    },
    getDeactivatedText(groupItems) {
      if (groupItems.length == 0) {
        return "";
      }

      const last = groupItems[groupItems.length - 1];

      return `${this.$i18n.t(
        "profile.mentorship.deactivate.deactivated"
      )} ${this.$options.filters.date(last.mentorship.deactivatedDateTime)} (${
        last.mentorship.deactivatedByEmployeeId
      })
      ${this.$i18n.t("profile.mentorship.deactivate.reason")}:
      ${last.mentorship.reason}`;
    },
  },
};
</script>
